import React, { useCallback, useContext, useEffect, useState } from 'react'

import moment from 'moment'
import { Tabs } from 'antd'
import { useSelector } from 'react-redux'

import InternshipList from './InternshipsList'
import { StudentContext } from '../../../Providers/Student/StudentProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { getTimezoneLessMoment } from '../../../utils/momentjs'
import { getStudentInternships } from '../../../utils/api/student'
import ShiftsCalendar from '../../../Components/student/Desiderata/ShiftsCalendar'
import InternshipRequestButton from './InternshipRequestButton'
import IndividualInternshipRequestModal from '../../../Providers/Forms/IndividualInternshipRequest/IndividualInternshipRequestModal'
import InternshipRequestsList from './InternshipRequestsList'
import DefaultContainer from '../../../HOC/DefaultContainer'
import { RequestPortalContext } from '../../../Providers/RequestPortalProvider'
import useSessionStorage from '../../../hooks/UseSessionStorage'
import { getIdOrNull } from '../../../utils'
import AppLoading from '../../../Components/shared/AppLoading'
import { deleteInternshipRequest, getAllInternshipRequests } from '../../../utils/api/internshipRequest'
import useDebounce from '../../../hooks/useDebounce'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { HttpStatuses } from '../../../utils/http'
import ScheduleChatRooms from '../../../Providers/ScheduleChatRooms'

import '../../../assets/student-internships-view.scss'

const SERIALISATION_CONTEXTS = ['institution', 'sector', 'section', 'institution', 'school', 'documents', 'coordinator']
const TabPane = Tabs.TabPane

export const STUDENT_INTERNSHIP_VIEW_ACTIVE_TAB_KEY = 'student.internships_view_active_key'

/** @enum {string} */
export const StudentInternshipsViewTabs = Object.freeze({
  INTERNSHIPS: '1',
  REQUESTS: '2',
  PAST_INTERNSHIPS: '3'
})

const InternshipsView = () => {
  const { loading: globalLoading, institutions, schools, sectors, student } = useContext(StudentContext)
  const { institution } = useContext(RequestPortalContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [activeTab, setActiveTab] = useSessionStorage(STUDENT_INTERNSHIP_VIEW_ACTIVE_TAB_KEY + user.id, StudentInternshipsViewTabs.INTERNSHIPS)

  const [internships, setInternships] = useState([])
  const [selectedInternship, setSelectedInternship] = useState(null)
  const [internshipRequests, setInternshipRequests] = useState({ data: [], loading: false })
  const [loading, setLoading] = useState(true)

  const useInternshipRequest = schools.reduce((acc, school) => {
    if (!acc && school.premium) {
      acc = true
    }

    return acc
  }, false)

  const isLoading = globalLoading || loading
  const sortedInternships = internships.sort((a, b) => moment(a.startDate).diff(moment(b.startDate))).reduce((acc, internship) => {
    if (getTimezoneLessMoment(internship.endDate).diff(moment()) < 0) {
      acc.past.push(internship)
    } else {
      acc.current.push(internship)
    }

    return acc
  }, { current: [], past: [] })

  const fetchInternshipRequests = useCallback(() => {
    setInternshipRequests(i => ({ ...i, loading: true }))

    getAllInternshipRequests(user).then(({ data }) => setInternshipRequests(i => ({ data, loading: false })))
  }, [user.id])

  useEffect(() => {
    fetchInternshipRequests()
  }, [fetchInternshipRequests])

  useEffect(() => {
    if (getIdOrNull(institution)) {
      setActiveTab(StudentInternshipsViewTabs.REQUESTS)
    }
  }, [institution])

  useEffect(() => {
    if (user.student) {
      setLoading(true)

      getStudentInternships(user, user.student, { contexts: SERIALISATION_CONTEXTS }).then(json => {
        if (json?.data) {
          setInternships(json.data)
        }

        setLoading(false)
      })
    } else {
      setInternships([])
    }
  }, [user.student])

  const updateSelectedInternship = internship => {
    const internshipsMap = internships.map(i => i.id === internship.id ? internship : i)

    setInternships(internshipsMap)
    setSelectedInternship(internship)
  }

  const deleteIR = internshipRequest => {
    deleteInternshipRequest(user, internshipRequest).then(() => {
      setInternshipRequests(i => ({ ...i, data: i.data.filter(ir => ir.id !== internshipRequest.id) }))

      onSuccess(t('internship_request.delete.success'))
    }).catch(error => {
      if (error.status === HttpStatuses.NOT_FOUND) {
        onError(t('internship_request.delete.already_deleted'))
      } else {
        onError(t('internship_request.delete.error'))
      }
    })
  }

  const handleDelete = useDebounce(deleteIR, 200)

  return (
    <div>
      {selectedInternship === null && (
        <IndividualInternshipRequestModal
          institutions={institutions}
          schools={{ data: schools, loading: false }}
          sectors={sectors}
        >
          <Tabs activeKey={activeTab} onChange={setActiveTab} animated={false} style={{ padding: '3px' }}>
            <TabPane tab={t('Upcoming internships')} key={StudentInternshipsViewTabs.INTERNSHIPS}>
              {isLoading && t('common.loading')}
              {!isLoading && (
                <InternshipList
                  internships={sortedInternships.current}
                  onSelect={id => setSelectedInternship(internships.find(i => i.id === id))}
                  onUpdate={internship => setInternships(internships.map(i => i.id === internship.id ? internship : i))}
                />
              )}
              {!isLoading && useInternshipRequest && (
                <InternshipRequestButton student={student} reload={fetchInternshipRequests} />
              )}
            </TabPane>
            <TabPane tab={t('student.internships_view.requests_title')} key={StudentInternshipsViewTabs.REQUESTS}>
              <DefaultContainer>
                {globalLoading ? (
                  <AppLoading />
                ) : (
                  <InternshipRequestsList
                    institution={institution}
                    internshipRequests={internshipRequests.data}
                    student={student}
                    reload={fetchInternshipRequests}
                    onDelete={handleDelete}
                  />
                )}
              </DefaultContainer>
            </TabPane>
            <TabPane tab={t('Past internships')} key={StudentInternshipsViewTabs.PAST_INTERNSHIPS}>
              {isLoading && t('common.loading')}
              {!isLoading && (
                <InternshipList
                  internships={sortedInternships.past}
                  onSelect={id => setSelectedInternship(internships.find(i => i.id === id))}
                  onUpdate={internship => setInternships(internships.map(i => i.id === internship.id ? internship : i))}
                />
              )}
            </TabPane>
          </Tabs>
        </IndividualInternshipRequestModal>
      )}
      {selectedInternship && (
        <ScheduleChatRooms>
          <ShiftsCalendar
            internship={selectedInternship}
            onBack={() => setSelectedInternship(null)}
            onInternshipUpdate={updateSelectedInternship}
          />
        </ScheduleChatRooms>
      )}
    </div>
  )
}

export default InternshipsView
